import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import s from './ScoreBadge.module.css';
import { Tooltip } from './Tooltip';

export type ScoreBand = 'excellent' | 'good' | 'fair' | 'poor' | 'bad' | 'none';

/** Bands from the old console's scoreColor.js, unchanged (style reference §8.4). */
export function scoreBand(score: number | null | undefined): ScoreBand {
  if (score == null || Number.isNaN(score)) return 'none';
  if (score >= 80) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 60) return 'fair';
  if (score >= 40) return 'poor';
  return 'bad';
}

/**
 * A 0–100 score as a tinted chip. `null` means "not observable" (too little exposure) and is
 * shown as "—", never as 0 or 100 (RULES §A-7). The colour always travels with the number.
 */
export function ScoreBadge({
  score,
  size = 'md',
  label,
  reason,
}: {
  score: number | null | undefined;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  /** Why the score is not observable (shown in the tooltip instead of the generic text). */
  reason?: string;
}) {
  const band = scoreBand(score);
  if (band === 'none') {
    return (
      <Tooltip content={reason ?? t('Not observable: not enough driving to score')}>
        <span className={cx(s.badge, s.none, s[size])} tabIndex={0} aria-label={label ? `${label}: ${t('not observable')}` : t('not observable')}>
          —
        </span>
      </Tooltip>
    );
  }
  const value = Math.round(score as number);
  return (
    <span className={cx(s.badge, s[band], s[size])} aria-label={label ? `${label}: ${value}` : undefined}>
      {value}
    </span>
  );
}
